import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Seo, { breadcrumbLd } from "@/components/seo/Seo";
import { BRAND, LAB } from "@/content/brand";
import { useSiteSettings, pick } from "@/lib/siteSettings";

const SITE_URL =
  process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const PATH = "/leadership";

/**
 * Strategic advisors. Add a `bio` (plain text, verified facts only) and a
 * `photo` URL once supplied; the card renders them when present.
 */
const ADVISORS = [
  { name: "Sakshi Kadle", role: "Strategic Advisor" },
  { name: "Apeksha Kadle", role: "Strategic Advisor" },
  { name: "Airaa Kadle", role: "Strategic Advisor" },
];

const GOVERNANCE = [
  {
    title: "Food safety and regulatory compliance",
    body: `Karijeeva is manufactured under FSSAI licence ${BRAND.fssai}. Every batch is tested at an independent food testing laboratory before release, and our latest sample met the FSSAI standard for coconut oil on all six quality parameters. Results are published on our lab reports page.`,
    link: { to: "/lab-reports", label: "Lab reports" },
  },
  {
    title: "Truthful communication",
    body: "All product information is prepared in line with the FSSAI (Advertising and Claims) Regulations, 2018. We make no disease prevention or cure claims, we cite the sources behind what we publish, and we do not display reviews, ratings or endorsements that have not been genuinely earned.",
    link: { to: "/cold-pressed-coconut-oil-benefits", label: "How we write about benefits" },
  },
  {
    title: "Traceability",
    body: "Coconuts are sourced from farms owned by the founding family in Tiptur and on the Uttara Kannada coast. Each jar carries a batch number that links it to its production and test records.",
    link: { to: "/the-farm", label: "Our farms" },
  },
  {
    title: "Customer protection",
    body: "Clear shipping, return and refund terms are published before purchase. Payments are processed by Razorpay; we never store card details.",
    link: { to: "/returns-policy", label: "Returns and refunds" },
  },
  {
    title: "Data privacy",
    body: "Personal data is used only to fulfil orders and support customers. Analytics cookies are set only with consent, and personal details are never shared with analytics providers.",
    link: { to: "/privacy-policy", label: "Privacy policy" },
  },
];

function Initials({ name }) {
  const letters = name.split(" ").map((w) => w[0]).join("").slice(0, 2);
  return (
    <div className="h-20 w-20 rounded-full bg-brand-obsidian text-white flex items-center justify-center font-display text-[26px] tracking-[-0.02em]" aria-hidden="true">
      {letters}
    </div>
  );
}

export default function Leadership() {
  const settings = useSiteSettings();
  const company = pick(settings, "company_name");
  const cin = pick(settings, "cin");
  const office = pick(settings, "registered_address");
  const legalEmail = pick(settings, "legal_email");
  const supportEmail = pick(settings, "support_email");
  const supportPhone = pick(settings, "support_phone");

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: BRAND.name,
      legalName: company,
      url: SITE_URL,
      member: ADVISORS.map((a) => ({
        "@type": "OrganizationRole",
        roleName: a.role,
        member: { "@type": "Person", name: a.name, jobTitle: a.role, worksFor: { "@type": "Organization", name: company } },
      })),
    },
    breadcrumbLd([{ name: "Home", path: "/" }, { name: "Leadership and governance", path: PATH }]),
  ];

  const facts = [
    ["Legal name", company],
    ["Brand", BRAND.name],
    ["Corporate Identification Number", cin],
    ["Registered office", office],
    ["FSSAI licence", BRAND.fssai],
    ["Testing laboratory", `${LAB.name}, ${LAB.city}`],
    ["Legal and corporate enquiries", legalEmail],
  ].filter(([, v]) => v);

  return (
    <>
      <Seo
        title="Leadership and Governance"
        description={`Leadership, strategic advisors and governance at ${company}, the company behind Karijeeva cold pressed coconut oil: compliance, quality and corporate information.`}
        url={`${SITE_URL}${PATH}`}
        jsonLd={jsonLd}
      />

      <section className="bg-brand-obsidian text-brand-bone" data-testid="page-leadership">
        <div className="max-w-5xl mx-auto px-6 pt-16 sm:pt-24 pb-20">
          <nav aria-label="Breadcrumb" className="font-body text-[13px] text-brand-bone/60">
            <Link to="/" className="hover:underline">Home</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span>Leadership and governance</span>
          </nav>
          <p className="mt-8 font-body text-[15px] font-semibold text-brand-bone/70">{company}</p>
          <h1 className="mt-2 font-display text-white text-[clamp(2.6rem,7vw,5rem)] leading-[0.98] tracking-display">
            Leadership and governance.
          </h1>
          <p className="mt-6 max-w-3xl font-body text-[18px] sm:text-[20px] leading-relaxed text-brand-bone/75">
            {company} owns and operates the Karijeeva brand. The company is guided by a group of strategic advisors and run on a simple set of commitments: food safety, truthful communication, traceability and fair treatment of every customer.
          </p>
        </div>
      </section>

      <section className="bg-brand-parchment py-24" aria-labelledby="advisors">
        <div className="max-w-5xl mx-auto px-6">
          <p className="font-body text-[15px] font-semibold text-brand-gold">Strategic Advisors</p>
          <h2 id="advisors" className="mt-2 font-display text-[clamp(2rem,5vw,3.25rem)] leading-[1.04] tracking-display text-brand-ink max-w-3xl">
            Guiding the company's direction.
          </h2>
          <p className="mt-5 max-w-3xl font-body text-[18px] leading-relaxed text-ink-muted">
            Our strategic advisors counsel the company on long term direction, brand stewardship and growth, and help ensure that the values Karijeeva was founded on are upheld as the business grows.
          </p>
          <ul className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ADVISORS.map((a) => (
              <li key={a.name} className="rounded-3xl bg-white p-8 flex flex-col" data-testid={`advisor-${a.name.toLowerCase().replace(/\s+/g, "-")}`}>
                {a.photo ? (
                  <img src={a.photo} alt={a.name} className="h-20 w-20 rounded-full object-cover" />
                ) : (
                  <Initials name={a.name} />
                )}
                <h3 className="mt-6 font-display text-[24px] tracking-[-0.02em] text-brand-ink">{a.name}</h3>
                <p className="mt-1 font-body text-[15px] text-brand-gold">{a.role}</p>
                <p className="mt-1 font-body text-[14px] text-ink-muted">{company}</p>
                {a.bio && <p className="mt-4 font-body text-[15px] leading-relaxed text-ink-muted">{a.bio}</p>}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-brand-parchment-soft py-24" aria-labelledby="governance">
        <div className="max-w-5xl mx-auto px-6">
          <p className="font-body text-[15px] font-semibold text-brand-gold">Governance</p>
          <h2 id="governance" className="mt-2 font-display text-[clamp(2rem,5vw,3.25rem)] leading-[1.04] tracking-display text-brand-ink max-w-3xl">
            How we hold ourselves to account.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 gap-4">
            {GOVERNANCE.map((g) => (
              <div key={g.title} className="rounded-3xl bg-white p-8 flex flex-col">
                <h3 className="font-display text-[22px] tracking-[-0.02em] text-brand-ink">{g.title}</h3>
                <p className="mt-3 font-body text-[16px] leading-relaxed text-ink-muted">{g.body}</p>
                <Link to={g.link.to} className="mt-auto pt-5 inline-flex items-center gap-1 font-body text-[15px] text-brand-gold hover:underline">
                  {g.link.label} <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-parchment py-24">
        <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-2 gap-12">
          <div aria-labelledby="corporate">
            <p className="font-body text-[15px] font-semibold text-brand-gold">Corporate information</p>
            <h2 id="corporate" className="mt-2 font-display text-[32px] tracking-heading text-brand-ink">Company details</h2>
            <dl className="mt-6 divide-y divide-black/10 border-y border-black/10">
              {facts.map(([k, v]) => (
                <div key={k} className="py-4 grid grid-cols-[1fr_1.3fr] gap-4">
                  <dt className="font-body text-[14px] text-ink-muted">{k}</dt>
                  <dd className="font-body text-[15px] text-brand-ink break-words">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div aria-labelledby="grievance">
            <p className="font-body text-[15px] font-semibold text-brand-gold">Grievance redressal</p>
            <h2 id="grievance" className="mt-2 font-display text-[32px] tracking-heading text-brand-ink">Raising a concern</h2>
            <p className="mt-4 font-body text-[16px] leading-relaxed text-ink-muted">
              In line with the Consumer Protection (E Commerce) Rules, 2020, we acknowledge every complaint within 48 hours and aim to resolve it within one month of receipt. Please include your order number, or the batch number printed on your jar.
            </p>
            <dl className="mt-6 divide-y divide-black/10 border-y border-black/10 font-body text-[15px]">
              {supportEmail && (
                <div className="py-4 grid grid-cols-[1fr_1.3fr] gap-4">
                  <dt className="text-ink-muted text-[14px]">Email</dt>
                  <dd><a href={`mailto:${supportEmail}`} className="text-brand-ink hover:underline">{supportEmail}</a></dd>
                </div>
              )}
              {supportPhone && (
                <div className="py-4 grid grid-cols-[1fr_1.3fr] gap-4">
                  <dt className="text-ink-muted text-[14px]">Phone</dt>
                  <dd><a href={`tel:${supportPhone.replace(/[^+\d]/g, "")}`} className="text-brand-ink hover:underline">{supportPhone}</a></dd>
                </div>
              )}
              <div className="py-4 grid grid-cols-[1fr_1.3fr] gap-4">
                <dt className="text-ink-muted text-[14px]">Hours</dt>
                <dd className="text-brand-ink">{pick(settings, "hours_ist")}</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>
    </>
  );
}
